// components/dashboard/AddUnit/CreateLeaseInviteModal/CreateLeaseInviteModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import UIModal from '@/components/shared/UIModal';
import UIButton from '@/components/shared/UIButton';
import UIInput from '@/components/shared/UIInput';
import UIAlert from '@/components/shared/UIAlert';
import { useCreatePendingLease } from '@/hooks/useUnitLease';
import { searchTenantsForInviteRequest } from '@/api';
import UIAvatarImage from '@/components/shared/UIAvatarImage';

interface CreateLeaseInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: number;
  unitId: number;
  unitNumber: string;
  propertyName?: string;
  onSuccess: () => void;
}

interface FormData {
  email: string;
  monthlyRent: string;
}

interface FormErrors {
  email?: string;
  monthlyRent?: string;
  general?: string;
}

interface TenantSearchResult {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  avatarFile?: {
    filePath: string;
  };
}

const CreateLeaseInviteModal: React.FC<CreateLeaseInviteModalProps> = ({
                                                                         isOpen,
                                                                         onClose,
                                                                         propertyId,
                                                                         unitId,
                                                                         unitNumber,
                                                                         propertyName = '',
                                                                         onSuccess,
                                                                       }) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    monthlyRent: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Стани для живого пошуку
  const [searchResults, setSearchResults] = useState<TenantSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Хук для створення pending lease
  const createPendingLeaseMutation = useCreatePendingLease();

  // Ефект для обробки кліків поза випадаючим списком
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Очищення таймауту при розмонтуванні
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Функція пошуку тенантів
  const searchTenants = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await searchTenantsForInviteRequest(query, 10);
      const results = response.data;
      setSearchResults(results);
      setShowDropdown(results.length > 0);
    } catch (error) {
      console.error('Error searching tenants:', error);
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setSearchLoading(false);
    }
  };

  // Обробник зміни email поля з пошуком
  const handleEmailChange = (value: string) => {
    setFormData((prev) => ({ ...prev, email: value }));

    // Очищаємо попередні помилки
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }

    // Дебаунс для пошуку
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      searchTenants(value);
    }, 300); // 300ms затримка

    setSearchTimeout(timeout);
  };

  // Вибір тенанта зі списку
  const handleSelectTenant = (tenant: TenantSearchResult) => {
    setFormData((prev) => ({ ...prev, email: tenant.email }));
    setShowDropdown(false);
    setSearchResults([]);

    // Фокус на поле monthly rent
    setTimeout(() => {
      const rentInput = document.querySelector(
        'input[type="number"]',
      ) as HTMLInputElement;
      if (rentInput) {
        rentInput.focus();
      }
    }, 100);
  };

  // Валідація форми
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    const rentValue = parseFloat(formData.monthlyRent);
    if (!formData.monthlyRent.trim()) {
      newErrors.monthlyRent = 'Monthly rent is required';
    } else if (isNaN(rentValue) || rentValue <= 0) {
      newErrors.monthlyRent = 'Monthly rent must be a positive number';
    } else if (rentValue > 999999) {
      newErrors.monthlyRent = 'Monthly rent amount is too high';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Обробник відправки форми
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setErrors({});

    try {
      const leaseData = {
        propertyId,
        unitId,
        email: formData.email.trim(),
        monthlyRent: parseFloat(formData.monthlyRent),
      };

      console.log('Creating pending lease:', leaseData);

      const response = await createPendingLeaseMutation.mutateAsync(leaseData);

      console.log('Pending lease created successfully', response.data);

      // Показуємо повідомлення про успіх
      setSuccessMessage(
        `Lease invitation sent successfully to ${formData.email.trim()}`
      );
      setShowSuccess(true);

      // Очищаємо форму після короткої затримки
      setTimeout(() => {
        setFormData({ email: '', monthlyRent: '' });
        setErrors({});
        setSearchResults([]);
        setShowDropdown(false);
        setSuccessMessage('');
        setShowSuccess(false);
        onSuccess();
        onClose();
      }, 2000); // 2 секунди показуємо success message

    } catch (error: any) {
      console.error('Error creating pending lease:', error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create lease invitation. Please try again.';

      setErrors({ general: errorMessage });
    }
  };

  // Обробник закриття модалі
  const handleClose = () => {
    if (!createPendingLeaseMutation.isPending) {
      setFormData({ email: '', monthlyRent: '' });
      setErrors({});
      setSearchResults([]);
      setShowDropdown(false);
      setSuccessMessage('');
      setShowSuccess(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      onClose();
    }
  };

  // Обробник зміни monthly rent
  const handleRentChange = (value: string) => {
    setFormData((prev) => ({ ...prev, monthlyRent: value }));
    if (errors.monthlyRent) {
      setErrors((prev) => ({ ...prev, monthlyRent: undefined }));
    }
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }
  };

  return (
    <UIModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Create Lease for Unit ${unitNumber}`}
      size="small"
      closeOnBackdropClick={!createPendingLeaseMutation.isPending}
      closeOnEscape={!createPendingLeaseMutation.isPending}
    >
      <div style={{ width: '100%', padding: '16px 0' }}>
        <p
          style={{
            margin: '0 0 24px 0',
            fontSize: '14px',
            color: '#637381',
            lineHeight: '20px',
          }}
        >
          Create a lease agreement for unit {unitNumber}
          {propertyName && ` in ${propertyName}`}. This will create a pending
          lease and send an invitation to the tenant.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Email поле з живим пошуком */}
          <div
            style={{ marginBottom: '20px', position: 'relative' }}
            ref={dropdownRef}
          >
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#161C24',
              }}
            >
              Tenant Email *
            </label>
            <UIInput
              ref={inputRef}
              type="email"
              value={formData.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="Enter tenant's email address"
              disabled={createPendingLeaseMutation.isPending}
              style={{ width: '100%' }}
              autoComplete="off"
            />

            {/* Випадаючий список з результатами пошуку */}
            {showDropdown && (searchResults.length > 0 || searchLoading) && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              >
                {searchLoading ? (
                  <div
                    style={{
                      padding: '12px 16px',
                      color: '#637381',
                      fontSize: '14px',
                    }}
                  >
                    Searching...
                  </div>
                ) : (
                  searchResults.map((tenant) => (
                    <div
                      key={tenant.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #F5F5F5',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                      onClick={() => handleSelectTenant(tenant)}
                      onMouseEnter={(e) => {
                        (e.target as HTMLElement).style.backgroundColor =
                          '#F8F9FA';
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLElement).style.backgroundColor =
                          'white';
                      }}
                    >
                      <UIAvatarImage
                        avatar={tenant.avatarFile?.filePath}
                        alt={`${tenant.firstname} ${tenant.lastname}`}
                        size="small"
                      />
                      <div>
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#161C24',
                          }}
                        >
                          {tenant.firstname} {tenant.lastname}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#637381',
                          }}
                        >
                          {tenant.email}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {errors.email && (
              <div
                style={{
                  color: '#FB2F54',
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                {errors.email}
              </div>
            )}
          </div>

          {/* Monthly Rent поле */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#161C24',
              }}
            >
              Monthly Rent ($) *
            </label>
            <UIInput
              type="number"
              value={formData.monthlyRent}
              onChange={(e) => handleRentChange(e.target.value)}
              placeholder="Enter monthly rent amount"
              disabled={createPendingLeaseMutation.isPending}
              style={{ width: '100%' }}
              min="0"
              step="0.01"
            />
            {errors.monthlyRent && (
              <div
                style={{
                  color: '#FB2F54',
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                {errors.monthlyRent}
              </div>
            )}
          </div>

          {/* Success повідомлення */}
          {showSuccess && successMessage && (
            <div style={{ marginBottom: '20px' }}>
              <UIAlert variant="success" fullWidth>
                {successMessage}
              </UIAlert>
            </div>
          )}

          {/* Загальна помилка */}
          {errors.general && (
            <div
              style={{
                color: '#FB2F54',
                fontSize: '14px',
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#FEF2F2',
                borderRadius: '8px',
                border: '1px solid #FECACA',
              }}
            >
              {errors.general}
            </div>
          )}

          {/* Кнопки */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              paddingTop: '8px',
            }}
          >
            <UIButton
              type="button"
              variant="primary"
              outline
              size="medium"
              onClick={handleClose}
              disabled={createPendingLeaseMutation.isPending}
            >
              Cancel
            </UIButton>

            <UIButton
              type="submit"
              variant="primary"
              size="medium"
              disabled={
                createPendingLeaseMutation.isPending ||
                !formData.email.trim() ||
                !formData.monthlyRent.trim()
              }
            >
              {createPendingLeaseMutation.isPending
                ? 'Creating Lease...'
                : 'Create Lease & Send Invite'}
            </UIButton>
          </div>
        </form>
      </div>
    </UIModal>
  );
};

export default CreateLeaseInviteModal;
